/**
 * The fixed parts of every templated SMS, and segment/billing math.
 *
 * 10DLC/CTIA require sender identification and a working opt-out in the message body, so the
 * company prefix and the STOP line are composed here and are never part of a stored template —
 * a user editing a template cannot remove them.
 */

export const SMS_OPT_OUT = 'Reply STOP to opt out';

/** `Acme Plumbing: <body>\nReply STOP to opt out` */
export function composeSms(companyName: string, body: string): string {
    const prefix = companyName ? `${companyName}: ` : '';
    return `${prefix}${body}\n${SMS_OPT_OUT}`;
}

// GSM-7 default alphabet. A body made only of these is billed at 160 chars (153 per segment when
// split); one character outside it flips the WHOLE message to UCS-2 at 70 (67 per segment), so
// counting against 160 would understate the bill by more than 2x.
const GSM7_BASIC =
    '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
    '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
// These cost two GSM-7 characters each (escape + char).
const GSM7_EXTENDED = '^{}\\[~]|€';

export interface SmsLength {
    encoding: 'GSM-7' | 'UCS-2';
    /** Billable units: GSM-7 extended characters count as 2. */
    chars: number;
    segments: number;
}

export function smsSegments(text: string): SmsLength {
    let units = 0;
    let gsm7 = true;

    for (const ch of text) {
        if (GSM7_BASIC.includes(ch)) {
            units += 1;
        } else if (GSM7_EXTENDED.includes(ch)) {
            units += 2;
        } else {
            gsm7 = false;
            break;
        }
    }

    if (!gsm7) {
        // UCS-2 counts UTF-16 code units, so an emoji (surrogate pair) costs 2.
        const units16 = text.length;
        return {
            encoding: 'UCS-2',
            chars: units16,
            segments: units16 <= 70 ? 1 : Math.ceil(units16 / 67),
        };
    }

    return {
        encoding: 'GSM-7',
        chars: units,
        segments: units <= 160 ? 1 : Math.ceil(units / 153),
    };
}

/** The characters that would flip a message to UCS-2, for a "this doubles your cost" warning. */
export function nonGsm7Characters(text: string): string[] {
    return [...new Set([...text].filter((ch) => !GSM7_BASIC.includes(ch) && !GSM7_EXTENDED.includes(ch)))];
}
